import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  IconRevenue,
  IconCart,
  IconBox,
  IconUsers,
  IconChart,
  IconLayers,
  IconTrendUp,
  IconTarget,
  IconBriefcase,
} from '../components/Icons';
import './Dashboard.css';
import './Common.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchDashboardData();
  }, [period]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/dashboard/stats?period=${period}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `${Number(value || 0).toFixed(2)}`;
  };

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString();
  };

  const formatPercentage = (value) => {
    const num = Number(value || 0);
    return `${num > 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  const COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2'];

  if (loading) {
    return <div className="loading-state">Chargement du tableau de bord...</div>;
  }

  if (!stats) {
    return <div className="loading-state">Aucune donnée disponible</div>;
  }

  // Prepare order status pie chart data
  const statusTranslations = {
    pending: 'En attente',
    confirmed: 'Confirmé',
    processing: 'En cours',
    shipped: 'Expédié',
    delivered: 'Livré',
    cancelled: 'Annulé'
  };

  const orderStatusData = Object.entries(stats?.orders || {})
    .filter(([key]) => !['total'].includes(key))
    .map(([key, value]) => ({
      name: statusTranslations[key] || key.charAt(0).toUpperCase() + key.slice(1),
      value: value
    }))
    .filter(item => item.value > 0);

  // Prepare top selling products data
  const topProductsData = (stats?.top_selling_products || []).map(item => ({
    name: item.product?.ref || 'Inconnu',
    sold: item.total_sold,
    orders: item.order_count
  }));

  // Prepare inventory overview
  const inventoryData = [
    { name: 'En Stock', value: stats?.overview?.in_stock_products || 0, color: '#16a34a' },
    { name: 'Stock Faible', value: stats?.overview?.low_stock_products || 0, color: '#d97706' },
    { name: 'Rupture de Stock', value: stats?.overview?.out_of_stock_products || 0, color: '#dc2626' }
  ].filter(item => item.value > 0);

  return (
    <div className="dashboard-page">
      {/* Header with Period Selector */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Tableau de Bord</h1>
          <p className="dashboard-subtitle">Bienvenue ! Voici ce qui se passe dans votre magasin.</p>
        </div>
        <div className="period-selector">
          {[
            { key: 'today', label: "Aujourd'hui" },
            { key: 'week', label: 'Semaine' },
            { key: 'month', label: 'Mois' },
            { key: 'year', label: 'Année' }
          ].map(p => (
            <button
              key={p.key}
              className={period === p.key ? 'active' : ''}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="stats-grid">
        {/* Revenue Card */}
        <div className="stat-card">
          <div className="stat-icon revenue"><IconRevenue /></div>
          <div className="stat-content">
            <div className="stat-label">Revenu Total</div>
            <div className="stat-value">
              {formatCurrency(stats?.sales?.current_period?.revenue)}
            </div>
            <div className={`stat-trend ${stats?.sales?.growth?.revenue_percentage >= 0 ? 'positive' : 'negative'}`}>
              {formatPercentage(stats?.sales?.growth?.revenue_percentage)} vs période précédente
            </div>
          </div>
        </div>

        {/* Orders Card */}
        <div className="stat-card">
          <div className="stat-icon orders"><IconCart /></div>
          <div className="stat-content">
            <div className="stat-label">Commandes Totales</div>
            <div className="stat-value">{formatNumber(stats?.overview?.total_orders)}</div>
            <div className="stat-meta">
              <span className="badge pending">{stats?.orders?.pending || 0} en attente</span>
              <span className="badge processing">{stats?.orders?.processing || 0} en cours</span>
            </div>
          </div>
        </div>

        {/* Products Card */}
        <div className="stat-card">
          <div className="stat-icon products"><IconBox /></div>
          <div className="stat-content">
            <div className="stat-label">Produits Totaux</div>
            <div className="stat-value">{formatNumber(stats?.overview?.total_products)}</div>
            <div className="stat-meta">
              <span className="badge active">{stats?.overview?.in_stock_products || 0} en stock</span>
              <span className="badge warning">{stats?.overview?.low_stock_products || 0} faible</span>
            </div>
          </div>
        </div>

        {/* Users Card */}
        <div className="stat-card">
          <div className="stat-icon users"><IconUsers /></div>
          <div className="stat-content">
            <div className="stat-label">Utilisateurs Totaux</div>
            <div className="stat-value">{formatNumber(stats?.overview?.total_users)}</div>
            <div className="stat-meta">
              {stats?.overview?.new_users || 0} nouveaux cette période
            </div>
          </div>
        </div>

        {/* Average Order Value */}
        <div className="stat-card">
          <div className="stat-icon avg"><IconChart /></div>
          <div className="stat-content">
            <div className="stat-label">Valeur Moy. Commande</div>
            <div className="stat-value">
              {formatCurrency(stats?.sales?.current_period?.average_order_value)}
            </div>
            <div className="stat-meta">
              {formatNumber(stats?.sales?.current_period?.orders)} commandes passées
            </div>
          </div>
        </div>

        {/* Inventory Value */}
        <div className="stat-card">
          <div className="stat-icon inventory"><IconLayers /></div>
          <div className="stat-content">
            <div className="stat-label">Valeur Stock</div>
            <div className="stat-value">
              {formatCurrency(stats?.products?.total_inventory_value)}
            </div>
            <div className="stat-meta">
              {formatNumber(stats?.products?.total_quantity)} unités
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Revenue Trend Chart */}
        <div className="chart-card large">
          <div className="chart-header">
            <h3>Tendance des Revenus</h3>
            <p className="chart-subtitle">Revenus et volume de commandes dans le temps</p>
          </div>
          <div className="chart-content">
            {stats?.revenue_chart && stats.revenue_chart.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.revenue_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="time_unit" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 12px -2px rgba(15, 23, 42, 0.08)', fontSize: '13px' }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={false} name="Revenus" />
                  <Line type="monotone" dataKey="order_count" stroke="#16a34a" strokeWidth={2} dot={false} name="Commandes" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">Aucune donnée de revenus disponible</div>
            )}
          </div>
        </div>

        {/* Order Status Distribution */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>État des Commandes</h3>
            <p className="chart-subtitle">Répartition par statut</p>
          </div>
          <div className="chart-content">
            {orderStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">Aucune commande pour le moment</div>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Charts Row */}
      <div className="charts-row">
        {/* Top Selling Products */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Produits les Plus Vendus</h3>
            <p className="chart-subtitle">Meilleures performances cette période</p>
          </div>
          <div className="chart-content">
            {topProductsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topProductsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 12px -2px rgba(15, 23, 42, 0.08)', fontSize: '13px' }}
                  />
                  <Legend />
                  <Bar dataKey="sold" fill="#2563eb" name="Unités Vendues" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="orders" fill="#16a34a" name="Commandes" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">Aucune donnée de vente disponible</div>
            )}
          </div>
        </div>

        {/* Inventory Overview */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Aperçu de l'Inventaire</h3>
            <p className="chart-subtitle">Répartition de l'état du stock</p>
          </div>
          <div className="chart-content">
            {inventoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={inventoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({name, value}) => `${name}: ${value}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {inventoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-state">Aucune donnée d'inventaire</div>
            )}
          </div>
        </div>
      </div>

      {/* Data Tables Row */}
      <div className="tables-row">
        {/* Recent Orders */}
        <div className="activity-section">
          <div className="section-header">
            <h3>Commandes Récentes</h3>
            <p className="section-subtitle">Dernières commandes clients</p>
          </div>
          {stats?.recent_orders && stats.recent_orders.length > 0 ? (
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Code Commande</th>
                    <th>Client</th>
                    <th>Téléphone</th>
                    <th>Produit</th>
                    <th>Qté</th>
                    <th>Montant</th>
                    <th>Statut</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_orders.map((order) => (
                    <tr key={order.id}>
                      <td><strong>{order.order_code?.substring(0, 10)}...</strong></td>
                      <td>{order.customer_name}</td>
                      <td>{order.customer_phone}</td>
                      <td>{order.product?.ref || 'N/A'}</td>
                      <td>{order.quantity}</td>
                      <td><strong>{formatCurrency(order.total_price)}</strong></td>
                      <td>
                        <span className={`status-badge ${order.status}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">Aucune commande récente</div>
          )}
        </div>

        {/* Low Stock Alert */}
        <div className="activity-section">
          <div className="section-header">
            <h3>Alerte Stock Faible</h3>
            <p className="section-subtitle">Produits en rupture de stock</p>
          </div>
          {stats?.low_stock_products && stats.low_stock_products.length > 0 ? (
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Référence</th>
                    <th>Marque</th>
                    <th>Stock</th>
                    <th>Prix</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.low_stock_products.map((product) => (
                    <tr key={product.id}>
                      <td><strong>{product.ref}</strong></td>
                      <td>{product.marque_nom || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td>
                        <span className={`status-badge ${product.qntt <= 2 ? 'inactive' : 'warning'}`}>
                          {product.qntt} unités
                        </span>
                      </td>
                      <td>{formatCurrency(product.pv)}</td>
                      <td>
                        {product.qntt <= 2 ? (
                          <span className="alert-badge critical">Critique</span>
                        ) : (
                          <span className="alert-badge warning">Faible</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">Tous les produits sont bien approvisionnés.</div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-section">
        <div className="summary-card">
          <div className="summary-icon"><IconTrendUp /></div>
          <div className="summary-content">
            <h4>Performance des Ventes</h4>
            <p>
              {stats?.sales?.growth?.orders_percentage >= 0 ? 'Les commandes sont en hausse de' : 'Les commandes sont en baisse de'} {' '}
              <strong>{formatPercentage(Math.abs(stats?.sales?.growth?.orders_percentage))}</strong> par rapport à la période précédente.
            </p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon"><IconTarget /></div>
          <div className="summary-content">
            <h4>Statistiques Rapides</h4>
            <p>
              Vous avez <strong>{stats?.orders?.pending || 0} commandes en attente</strong> qui nécessitent une attention et{' '}
              <strong>{stats?.overview?.low_stock_products || 0} produits</strong> en stock faible.
            </p>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon"><IconBriefcase /></div>
          <div className="summary-content">
            <h4>Santé de l'Inventaire</h4>
            <p>
              Votre inventaire est évalué à <strong>{formatCurrency(stats?.products?.total_inventory_value)}</strong>{' '}
              sur <strong>{formatNumber(stats?.overview?.total_products)} produits</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
